import emitter from 'app/blocks/common/events/emitter';
import Events from 'app/blocks/common/events/Events';
import storage from 'app/blocks/common/storage';
import { isNonProd, isMock } from 'app/blocks/middleware/config';
import { appendInvitationTokenToParams, removeInvitationToken } from './register';
import { GET, POST } from './utils';

let user: User = { loggedIn: false };

const urls = {
    connectRedirectBaseUrl: null,
};

// export only for testing purpose
export function setAuthenticated(value: boolean) {
    if (storage.get('authenticated') === value) {
        return;
    }

    storage.set('authenticated', value);
}

export function getUser(): User {
    return user;
}

export async function refreshUser(): Promise<User> {
    user = await GET('/v2/loggedUserData');
    emitter.emit(Events.USER_CHANGED, user);
    setAuthenticated(user.loggedIn);

    return user;
}

export async function login(originParams) {
    const result = await POST('/authenticate', appendInvitationTokenToParams(originParams));
    removeInvitationToken();
    await refreshUser();

    return result;
}

export async function logout(stayOnPage = false) {
    try {
        await GET('/v2/logout');
    } finally {
        user = { loggedIn: false };
        setAuthenticated(false);

        if (stayOnPage) {
            emitter.emit(Events.USER_CHANGED, { loggedIn: false });
        } else {
            let env = 'nonprod';
            if (isMock()) {
                env = 'mock';
            } else if (!isNonProd()) {
                env = 'prod';
            }

            window.location.href = `/logout.html?env=${env}`;
        }
    }
}

export async function getConnectRedirectUrl() {
    if (!urls.connectRedirectBaseUrl) {
        urls.connectRedirectBaseUrl = await GET('/connect/redirect-url');
    }
    return urls.connectRedirectBaseUrl;
}

async function getConnectUrl() {
    return await GET('/connect/url');
}

export async function authenticateASLogin(originParams) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { action, ...params } = originParams;
    const result = await POST('/authenticate/connect', appendInvitationTokenToParams(params));
    removeInvitationToken();
    await refreshUser();

    return result;
}

const isURL = candidate => {
    try {
        const url = new URL(candidate);
        return !!url.hostname;
    } catch (e) {
        return false;
    }
};

function addLoginAction(href: string) {
    const url = new URL(href);
    url.searchParams.set('action', 'login');
    return url.href;
}

export function addRedirectUriParam(url: URL, relativePath: string, redirectBaseUrl: string) {
    const pathname = !!relativePath && relativePath !== '/' ? relativePath : '/index.html/dashboard';
    const redirect_uri = `${redirectBaseUrl}${pathname}`;
    url.searchParams.set('redirect_uri', addLoginAction(redirect_uri));
}

export async function getUiConnectUrl(relativePath?: string, isSkipRedirectURI?: boolean) {
    const silentAuthUrl = await getConnectUrl();
    const result = new URL(silentAuthUrl?.replace('&prompt=none', ''));
    if (!isSkipRedirectURI) {
        const redirectBaseUrl = await getConnectRedirectUrl();
        addRedirectUriParam(result, relativePath, redirectBaseUrl);
    }
    return result.href;
}

export async function getUrlWithRedirectURI(url: string, redirectPath?: string) {
    const redirectBaseUrl = await getConnectRedirectUrl();
    const result = isURL(url) ? new URL(url) : new URL(url, redirectBaseUrl);
    addRedirectUriParam(result, redirectPath, redirectBaseUrl);
    return result;
}

export function securityKey(url) {
    const randomString = isMock() ? 'MY_STATE' : (Math.random() + 1).toString(36).substring(7);
    localStorage.setItem('STATE_ID', randomString);
    return url.replace(/state=MY_STATE/, `state=${randomString}`);
}

storage.watch('authenticated', newValue => {
    setTimeout(() => {
        if (user?.loggedIn && !newValue) {
            logout(!window.location.href.includes('/index.html'));
        } else {
            refreshUser();
        }
    }, 0);
});
